/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getSuggestionsPortfolio } from '../../data/store/account/accountActions';

import { Button, Col, Row } from 'antd';
import CustomSearchInput from '../../components/CustomSearchInput/CustomSearchInput';

import { ReactComponent as Filter } from '../../assets/filter-icon.svg';
import { ReactComponent as Delete } from '../../assets/delete-black-icon.svg';
import { ReactComponent as Eye } from '../../assets/eye-icon.svg';
import { ReactComponent as Download } from '../../assets/export-icon.svg';
import { ReactComponent as Check } from '../../assets/check-orange-icon.svg';

import dayjs from 'dayjs';
import ModalViewPP from './ModalViewPP';
import { downloadReport } from '../../data/store/portfolio/portfolioActions';
import ModalDeleteCandidat from './ModalDeleteCandidat';
import ModalApproveCandidat from './ModalApproveCandidat';
import ModalFilterCandidat from './ModalFilterCandidat';
import CustomSortIcon from '../../components/CustomSortIcon/CustomSortIcon';
import { oneDecimalWithSpaces } from '../../helpers/formatFractional';
import { textSort } from '../../helpers/textSort';
import CustomTable from '../../components/CustomTable/CustomTable';

const SuggestionsTable = ({ userId }) => {
  const { t } = useTranslation('');
  const dispatch = useDispatch();

  const { suggestions, isLoading } = useSelector(
    (state) => state.accountReducer
  );
  const isLoadingPortfolio = useSelector(state => state.portfolioReducer?.isLoading);

  const [tableColumns, setTableColumns] = useState();
  const [filteredPortfolio, setFilteredPortfolio] = useState(null);
  const [filter, setFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterCounter, setFilterCounter] = useState(null);
  const [viewReport, setViewReport] = useState('');
  const [modalDeleteCandidat, setModalDeleteCandidat] = useState(0);
  const [modalApproveCandidat, setModalApproveCandidat] = useState(0);

  useEffect(() => {
    dispatch(getSuggestionsPortfolio(userId));
    return () => {
      dispatch({ type: 'CLEAR_SUGGESTIONS_PORTFOLIO' });
    };
  }, []);

  const applyFilters = () => {
    if (!filter) return setFilteredPortfolio(suggestions);
    const { current_cvar, current_mean_return, current_volatility, ...rest } = filter;
    const filteredByDate = suggestions.filter(item => {
      const createDate = dayjs(item.create_date);
      if (!rest.create_start_date && !rest.create_end_date) {
        return true;
      }
      if (!rest.create_start_date) {
        return createDate.isBefore(rest.create_end_date, 'day') || createDate.isSame(rest.create_end_date, 'day');
      }
      if (!rest.create_end_date) {
        return createDate.isAfter(rest.create_start_date, 'day') || createDate.isSame(rest.create_start_date, 'day');
      }
      return (createDate.isAfter(rest.create_start_date, 'day') || createDate.isSame(rest.create_start_date, 'day')) &&
              (createDate.isBefore(rest.create_end_date, 'day') || createDate.isSame(rest.create_end_date, 'day'));
    });

    const argsMinMax = { current_cvar, current_mean_return, current_volatility };
    const filteredArgs = filteredByDate.filter((item) => {
      for (const key in argsMinMax) {
        const { min, max } = argsMinMax[key];
        const value = item[key];

        if (min === 0 && max === 0) {
          continue;
        }
        if (min !== 0 && max === 0) {
          if (value < min) {
            return false;
          }
        }
        if (min === 0 && max !== 0) {
          if (value > max) {
            return false;
          }
        }
        if (min !== 0 && max !== 0) {
          if (value < min || value > max) {
            return false;
          }
        }
      }
      return true;
    });
    let filteredByPortfolio = [];
    if (rest.portfolioList[0] === 'All') {
      filteredByPortfolio = filteredArgs;
    } else {
      filteredByPortfolio = filteredArgs.filter(i => rest.portfolioList.includes(i.name));
    }
    setFilteredPortfolio(filteredByPortfolio);
  };

  useEffect(() => {
    applyFilters();
  }, [filter]);

  useEffect(() => {
    if (suggestions) {
      setFilteredPortfolio(suggestions);
    }
  }, [suggestions]);

  useEffect(() => {
    const defaultColumns = [
      {
        title: t('PORTFOLIO_NUMBER'),
        dataIndex: 'portfolio_code',
        className: 'suggestions-name',
        width: '',
        align: 'left',
        sorter: (a, b) => textSort(a.name, b.name),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('CREATION_DATE'),
        dataIndex: 'create_date',
        className: 'suggestions-create-date',
        width: '',
        align: 'left',
        render: (value) => <><span className='create-date'>{dayjs(value).format('MMM DD  YYYY')}</span>
                <br/><span className='suggestions-create-time'>{dayjs(value).format('HH:mm A')}</span></>,
        sorter: (a, b) => textSort(a.create_date, b.create_date),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('RETURN'),
        dataIndex: 'current_mean_return',
        className: 'suggestions-return',
        width: '',
        align: 'left',
        render: (value) => `${oneDecimalWithSpaces(value)}%`,
        sorter: (a, b) => a.current_mean_return - b.current_mean_return,
        sortDirections: ['descend', 'ascend'],
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('VOLATILITY'),
        dataIndex: 'current_volatility',
        className: 'suggestions-volatility',
        width: '',
        align: 'left',
        render: (value) => `${oneDecimalWithSpaces(value)}%`,
        sorter: (a, b) => a.current_volatility - b.current_volatility,
        sortDirections: ['descend', 'ascend'],
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: 'CVar',
        dataIndex: 'current_cvar',
        className: 'suggestions-cvar',
        width: '',
        align: 'left',
        render: (value) => `${oneDecimalWithSpaces(value)}%`,
        sorter: (a, b) => a.current_cvar - b.current_cvar,
        sortDirections: ['descend', 'ascend'],
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('ACTIONS'),
        align: 'center',
        dataIndex: 'operation',
        width: '15%',
        render: (_, record) => {
          return (
            <div className="cell-button-wrapper">
              <Button
                icon={<Check />}
                iconPosition='end'
                className="action-btn approve"
                onClick={() => handlApproval(record.id)}
              >
                {t('APPROVE')}
              </Button>
              <Button type="text" onClick={() => hendlePreview(record.report_link)}>
                <Eye />
              </Button>
              <Button type="text" onClick={() => hendleDownload(record.repot_id)} disabled={isLoadingPortfolio}>
                <Download />
              </Button>
              <Button type="text" onClick={() => handleDelete(record.id)}>
                <Delete />
              </Button>
            </div>
          );
        },
      },
    ];
    setTableColumns(defaultColumns);
  }, [suggestions, isLoadingPortfolio]);

  const handeleFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleSearch = (event) => {
    const { value } = event.target;
    if (value?.length > 2) {
      const searched = suggestions?.filter(
        (i) =>
          i.name?.toLowerCase().includes(value?.toLowerCase())
      );
      setFilteredPortfolio(searched);
    } else {
      setFilteredPortfolio(suggestions);
    }
  };

  const hendlePreview = (report_link) => {
    setViewReport(report_link);
  };

  const hendleDownload = (repot_id) => {
    dispatch(downloadReport(repot_id));
  };

  const handleDelete = (id) => {
    setModalDeleteCandidat(id);
  };

  const handlApproval = (id) => {
    setModalApproveCandidat(id);
  };

  const getCandidatNameById = useMemo(() => {
    return suggestions?.find(i => i.id === modalDeleteCandidat)?.name || '- - -';
  }, [suggestions, modalDeleteCandidat]);

  return (
    <div>
      <Row justify={'end'} className='search-wrapper'>
        <Col span={12} className="instrument-button-wraper">
          <CustomSearchInput
            className='account-select-search suggested'
            placeholder={t('SEARCH')}
            onChange={handleSearch}
          />
          <Button
            className="filter-btn"
            type="default"
            disabled={isLoading}
            onClick={handeleFilter}
            icon={<Filter />}
            iconPosition="end"
          >
            {t('FILTER')}
            {filterCounter
              ? (
              <span className="filter-counter">{filterCounter}</span>
                )
              : (
                  ''
                )}
          </Button>
        </Col>
      </Row>
      <div className="client portfolio-table suggestions-table">
        <CustomTable
          rowKey={(record) => record.id}
          loading={isLoading}
          columns={tableColumns}
          dataSource={filteredPortfolio}
          size="middle"
          pagination={false}
        />
      </div>
      <ModalViewPP
        viewReport={viewReport}
        setViewReport={setViewReport}
      />
      <ModalDeleteCandidat
        userId={userId}
        text='DELETE_THIS_SUGGESTED'
        candidatType={'suggestions'}
        name={getCandidatNameById}
        modalDeleteCandidat={modalDeleteCandidat}
        setModalDeleteCandidat={setModalDeleteCandidat}
      />
      <ModalApproveCandidat
        userId={userId}
        modalApproveCandidat={modalApproveCandidat}
        setModalApproveCandidat={setModalApproveCandidat}
      />
      <ModalFilterCandidat
        type='suggestions'
        source={suggestions}
        setFilter={setFilter}
        openFilter={openFilter}
        handleCloseFilter={handleCloseFilter}
      />
    </div>
  );
};

export default SuggestionsTable;

SuggestionsTable.propTypes = {
  userId: PropTypes.string,
  data: PropTypes.array,
};
